import { useEffect, useState, useCallback } from "react";
import {
  Users,
  RefreshCw,
  TrendingUp,
  BarChart3,
  Clock,
  Repeat2,
  ChevronRight,
  Tag,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import { API } from "../App";
import axios from "../lib/api";

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmtEur = (v) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(v || 0);

const fmtEur2 = (v) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v || 0);

const pct = (v) => `${(v || 0).toFixed(1)}%`;

// Heat-map color for cohort cells (green scale)
function ltvHeatColor(value, maxValue) {
  if (!value || !maxValue) return "";
  const ratio = Math.min(value / maxValue, 1);
  if (ratio >= 0.75) return "bg-emerald-500/25 text-emerald-300";
  if (ratio >= 0.5)  return "bg-emerald-500/15 text-emerald-400";
  if (ratio >= 0.25) return "bg-emerald-500/8  text-slate-300";
  return "text-slate-400";
}

// ─── Summary card ─────────────────────────────────────────────────────────────
const SummaryCard = ({ title, value, sub, icon: Icon, color = "text-slate-200" }) => (
  <div className="bg-slate-800/60 rounded-xl p-4 border border-slate-700/60">
    <div className="flex items-start justify-between mb-2">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</p>
      {Icon && <Icon className="w-4 h-4 text-slate-600" />}
    </div>
    <p className={`text-2xl font-black font-mono ${color}`}>{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

// ─── Repeat Distribution Bar ──────────────────────────────────────────────────
const RepeatBar = ({ label, count, pctValue, total, colorClass }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-slate-400 w-20 flex-shrink-0">{label}</span>
    <div className="flex-1 bg-slate-800 rounded-full h-2 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${colorClass}`}
        style={{ width: `${pctValue}%` }}
      />
    </div>
    <span className="text-xs font-mono text-slate-300 w-24 text-right flex-shrink-0">
      {count.toLocaleString("es-ES")} ({pct(pctValue)})
    </span>
  </div>
);

// ─── Main page ────────────────────────────────────────────────────────────────
export default function AnalyticsCustomerLTVPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Customer groups LTV
  const [groupData, setGroupData]       = useState(null);
  const [groupLoading, setGroupLoading] = useState(true);
  const [groupSyncing, setGroupSyncing] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/analytics/customer-ltv`, {
        params: { include_cohorts: true },
        withCredentials: true,
        headers: authHeaders(),
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Error al cargar datos de LTV");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGroupData = useCallback(async () => {
    try {
      setGroupLoading(true);
      const res = await axios.get(`${API}/analytics/customer-ltv/by-group`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setGroupData(res.data);
    } catch (err) {
      console.error(err);
      // Don't show error toast — groups might simply not be synced yet
    } finally {
      setGroupLoading(false);
    }
  }, []);

  const syncGroups = async () => {
    try {
      setGroupSyncing(true);
      await axios.post(`${API}/prestashop/sync-customer-groups`, {}, {
        withCredentials: true,
        headers: authHeaders(),
      });
      toast.success("Sincronización iniciada...");
      // Poll until complete — max 5 minutes (150 x 2s)
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await axios.get(`${API}/prestashop/customer-groups/status`, {
            withCredentials: true,
            headers: authHeaders(),
          });
          const { status, synced_profiles, batches_done, total_batches } = statusRes.data;

          // Show live progress toast while running
          if (status === "running" && total_batches) {
            const pct = Math.round((batches_done || 0) / total_batches * 100);
            toast.info(
              `Sincronizando grupos... ${synced_profiles?.toLocaleString() || 0} clientes (${pct}%)`,
              { id: "group-sync-progress", duration: 2500 }
            );
          }

          if (status === "completed" || status === "failed" || status === "timeout" || attempts > 150) {
            clearInterval(poll);
            setGroupSyncing(false);
            if (status === "completed") {
              toast.success(`Grupos sincronizados: ${(synced_profiles || 0).toLocaleString()} clientes`, { id: "group-sync-progress" });
              await fetchGroupData();
            } else if (status === "failed" || status === "timeout") {
              toast.error("Error al sincronizar grupos. Revisa los logs.", { id: "group-sync-progress" });
            } else {
              toast.warning("La sincronización tardó demasiado. Comprueba el estado.");
            }
          }
        } catch (_) {
          clearInterval(poll);
          setGroupSyncing(false);
        }
      }, 2000);
    } catch (err) {
      console.error(err);
      toast.error("Error al iniciar la sincronización");
      setGroupSyncing(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchGroupData();
  }, [fetchData, fetchGroupData]);

  // Max LTV for heatmap scale
  const maxLtvTotal = data?.cohort_table
    ? Math.max(...data.cohort_table.map((r) => r.ltv_total || 0), 1)
    : 1;

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">
              Customer Lifetime Value
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Análisis histórico total — ¿Cuánto vale un cliente a lo largo de su vida?
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-sm disabled:opacity-50"
            data-testid="ltv-refresh-btn"
          >            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Cargando datos de clientes...
          </div>
        )}

        {!loading && data && (
          <>
            {/* ── Summary KPIs ─────────────────────────────────────────── */}
            <div
              className="rounded-xl border border-slate-700 bg-slate-900 overflow-hidden"
              data-testid="ltv-summary-section"
            >
              <div className="px-5 py-3.5 border-b border-slate-800 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-slate-500" />
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                  Métricas Generales (Histórico Total)
                </span>
              </div>
              <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
                <SummaryCard
                  title="LTV Total"
                  value={fmtEur(data.ltv)}
                  sub="ingresos por cliente (histórico)"
                  icon={TrendingUp}
                  color="text-slate-200"
                />
                <SummaryCard
                  title="LTV 12 meses"
                  value={fmtEur(data.ltv_12m)}
                  sub="clientes activos últ. 12 meses"
                  icon={TrendingUp}
                  color="text-amber-400"
                />
                <SummaryCard
                  title="LTV Margen"
                  value={fmtEur(data.ltv_margin)}
                  sub="margen por cliente"
                  icon={TrendingUp}
                  color={data.ltv_margin > 0 ? "text-emerald-400" : "text-red-400"}
                />
                <SummaryCard
                  title="LTV 90 días"
                  value={fmtEur(data.ltv_90d)}
                  sub="primeros 90 días"
                  icon={Clock}
                  color="text-blue-400"
                />
                <SummaryCard
                  title="Pedidos / Cliente"
                  value={(data.orders_per_customer || 0).toFixed(1)}
                  sub="promedio histórico"
                  icon={BarChart3}
                  color="text-slate-200"
                />
                <SummaryCard
                  title="Repeat Rate"
                  value={pct(data.repeat_rate)}
                  sub="clientes con 2+ pedidos"
                  icon={Repeat2}
                  color="text-emerald-400"
                />
                <SummaryCard
                  title="CAC Payback"
                  value={data.cac_payback_days != null ? `${data.cac_payback_days}d` : "—"}
                  sub={data.cac_payback_days != null ? `~${(data.cac_payback_days / 30.44).toFixed(1)} meses` : "sin datos"}
                  icon={Clock}
                  color={
                    data.cac_payback_status === "excellent" ? "text-emerald-400" :
                    data.cac_payback_status === "good"      ? "text-blue-400"    :
                    data.cac_payback_status === "normal"    ? "text-amber-400"   :
                    data.cac_payback_status === "slow"      ? "text-red-400"     :
                    "text-slate-400"
                  }
                />
              </div>
            </div>

            {/* ── Cohort Table ─────────────────────────────────────────── */}
            {data.cohort_table && data.cohort_table.length > 0 && (
              <Card className="border-slate-200" data-testid="ltv-cohort-table">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-500" />
                    Análisis de Cohortes por Mes
                  </CardTitle>
                  <p className="text-xs text-slate-500">
                    LTV por cliente según el mes de su primer pedido. Cuanto más verde, mayor valor.
                  </p>
                </CardHeader>
                <CardContent className="overflow-x-auto p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 border-b border-slate-200">
                        <TableHead className="text-xs font-bold text-slate-600 py-3 pl-4">Cohorte</TableHead>
                        <TableHead className="text-xs font-bold text-slate-600 text-right py-3">Clientes</TableHead>
                        <TableHead className="text-xs font-bold text-slate-600 text-right py-3">LTV 30d</TableHead>
                        <TableHead className="text-xs font-bold text-slate-600 text-right py-3">LTV 60d</TableHead>
                        <TableHead className="text-xs font-bold text-slate-600 text-right py-3">LTV 90d</TableHead>
                        <TableHead className="text-xs font-bold text-slate-600 text-right py-3">LTV 180d</TableHead>
                        <TableHead className="text-xs font-bold text-slate-600 text-right py-3 pr-4">LTV Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...data.cohort_table].reverse().map((row) => (
                        <TableRow key={row.cohort_month} className="border-b border-slate-100 hover:bg-slate-50">
                          <TableCell className="py-2.5 pl-4 font-mono text-sm text-slate-700 font-semibold">
                            {row.cohort_month}
                          </TableCell>
                          <TableCell className="text-right py-2.5 text-sm text-slate-600">
                            {row.customers.toLocaleString("es-ES")}
                          </TableCell>
                          <TableCell className={`text-right py-2.5 text-sm font-mono rounded px-2 ${ltvHeatColor(row.ltv_30d, maxLtvTotal)}`}>
                            {fmtEur2(row.ltv_30d)}
                          </TableCell>
                          <TableCell className={`text-right py-2.5 text-sm font-mono rounded px-2 ${ltvHeatColor(row.ltv_60d, maxLtvTotal)}`}>
                            {fmtEur2(row.ltv_60d)}
                          </TableCell>
                          <TableCell className={`text-right py-2.5 text-sm font-mono rounded px-2 ${ltvHeatColor(row.ltv_90d, maxLtvTotal)}`}>
                            {fmtEur2(row.ltv_90d)}
                          </TableCell>
                          <TableCell className={`text-right py-2.5 text-sm font-mono rounded px-2 ${ltvHeatColor(row.ltv_180d, maxLtvTotal)}`}>
                            {fmtEur2(row.ltv_180d)}
                          </TableCell>
                          <TableCell className={`text-right py-2.5 text-sm font-mono font-bold pr-4 rounded px-2 ${ltvHeatColor(row.ltv_total, maxLtvTotal)}`}>
                            {fmtEur2(row.ltv_total)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* ── Repeat Distribution ──────────────────────────────────── */}
            {data.repeat_distribution && data.repeat_distribution.total > 0 && (
              <Card className="border-slate-200" data-testid="ltv-repeat-distribution">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <Repeat2 className="w-4 h-4 text-slate-500" />
                    Distribución de Recompra
                  </CardTitle>
                  <p className="text-xs text-slate-500">
                    {data.repeat_distribution.total.toLocaleString("es-ES")} clientes únicos en total
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <RepeatBar
                    label="1 pedido"
                    count={data.repeat_distribution.one_order}
                    pctValue={data.repeat_distribution.one_pct}
                    total={data.repeat_distribution.total}
                    colorClass="bg-slate-400"
                  />
                  <RepeatBar
                    label="2 pedidos"
                    count={data.repeat_distribution.two_orders}
                    pctValue={data.repeat_distribution.two_pct}
                    total={data.repeat_distribution.total}
                    colorClass="bg-blue-400"
                  />
                  <RepeatBar
                    label="3 pedidos"
                    count={data.repeat_distribution.three_orders}
                    pctValue={data.repeat_distribution.three_pct}
                    total={data.repeat_distribution.total}
                    colorClass="bg-emerald-400"
                  />
                  <RepeatBar
                    label="4+ pedidos"
                    count={data.repeat_distribution.four_plus}
                    pctValue={data.repeat_distribution.four_plus_pct}
                    total={data.repeat_distribution.total}
                    colorClass="bg-emerald-600"
                  />
                </CardContent>
              </Card>
            )}

            {/* No data state */}
            {!data.has_data && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-12 text-center">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">Sin datos de clientes</p>
                <p className="text-sm text-slate-400 mt-1">
                  Sincroniza pedidos con customer_id asignado para ver el análisis LTV.
                </p>
              </div>
            )}
          </>
        )}

        {/* ── LTV por Grupo de Cliente ──────────────────────────────── */}
        <div
          className="rounded-xl border border-slate-700 bg-slate-900 overflow-hidden"
          data-testid="ltv-by-group-section"
        >
          {/* Header */}
          <div className="px-5 py-3.5 border-b border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-slate-500" />
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                LTV por Grupo de Cliente
              </span>
              {groupData?.synced_at && (
                <span className="text-xs text-slate-600">
                  · Sync: {new Date(groupData.synced_at).toLocaleDateString("es-ES")}
                </span>
              )}
            </div>
            <button
              onClick={syncGroups}
              disabled={groupSyncing}
              data-testid="sync-groups-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-600 transition-all text-xs font-medium disabled:opacity-50"
            >
              {groupSyncing
                ? <><Loader2 className="w-3 h-3 animate-spin" /> Sincronizando...</>
                : <><RefreshCw className="w-3 h-3" /> Sincronizar grupos</>
              }
            </button>
          </div>

          {/* Content */}
          {groupLoading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Cargando datos por grupo...
            </div>
          ) : !groupData?.has_data ? (
            <div className="p-8 text-center">
              <Tag className="w-8 h-8 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 font-medium text-sm">
                {groupData?.total_customers_with_orders > 0
                  ? "Grupos no sincronizados todavía"
                  : "Sin datos de clientes disponibles"}
              </p>
              <p className="text-xs text-slate-600 mt-1 mb-4">
                {groupData?.total_customers_with_orders > 0
                  ? `Tienes ${groupData.total_customers_with_orders.toLocaleString("es-ES")} clientes con pedidos. Sincroniza para ver sus grupos.`
                  : "Sincroniza pedidos primero para ver el LTV por grupo."}
              </p>
              {groupData?.total_customers_with_orders > 0 && (
                <button
                  onClick={syncGroups}
                  disabled={groupSyncing}
                  data-testid="sync-groups-empty-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {groupSyncing
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Sincronizando...</>
                    : <><Tag className="w-4 h-4" /> Sincronizar grupos de clientes</>
                  }
                </button>
              )}
            </div>
          ) : (
            <div className="p-5 space-y-4">
              {/* Coverage indicator */}
              {groupData.total_customers_with_orders > 0 && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <AlertCircle className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
                  <span>
                    {groupData.profiled_customers.toLocaleString("es-ES")} de{" "}
                    {groupData.total_customers_with_orders.toLocaleString("es-ES")} clientes
                    clasificados en grupos
                    {groupData.profiled_customers < groupData.total_customers_with_orders && (
                      <> · <button onClick={syncGroups} className="text-blue-400 hover:underline">Re-sincronizar</button></>
                    )}
                  </span>
                </div>
              )}

              {/* Groups table */}
              <div className="overflow-x-auto rounded-lg border border-slate-800">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-800/80 border-b border-slate-700">
                      <TableHead className="text-xs font-bold text-slate-400 py-3 pl-4">Grupo</TableHead>
                      <TableHead className="text-xs font-bold text-slate-400 text-right py-3">Clientes</TableHead>
                      <TableHead className="text-xs font-bold text-slate-400 text-right py-3">LTV</TableHead>
                      <TableHead className="text-xs font-bold text-slate-400 text-right py-3">LTV 90d</TableHead>
                      <TableHead className="text-xs font-bold text-slate-400 text-right py-3">Pedidos/Cliente</TableHead>
                      <TableHead className="text-xs font-bold text-slate-400 text-right py-3 pr-4">Repeat Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupData.groups.map((g, idx) => (
                      <TableRow
                        key={g.group_name}
                        className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors"
                        data-testid={`group-row-${idx}`}
                      >
                        <TableCell className="py-3 pl-4">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500/60 flex-shrink-0" />
                            <span className="text-sm font-semibold text-slate-200">{g.group_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right py-3 text-sm font-mono text-slate-300">
                          {g.total_customers.toLocaleString("es-ES")}
                        </TableCell>
                        <TableCell className="text-right py-3 text-sm font-mono font-bold text-emerald-400">
                          {fmtEur2(g.ltv)}
                        </TableCell>
                        <TableCell className="text-right py-3 text-sm font-mono text-blue-400">
                          {fmtEur2(g.ltv_90d)}
                        </TableCell>
                        <TableCell className="text-right py-3 text-sm font-mono text-slate-300">
                          {g.orders_per_customer.toFixed(1)}
                        </TableCell>
                        <TableCell className="text-right py-3 pr-4 text-sm font-mono">
                          <span className={
                            g.repeat_rate >= 40 ? "text-emerald-400" :
                            g.repeat_rate >= 20 ? "text-amber-400" :
                            "text-slate-400"
                          }>
                            {pct(g.repeat_rate)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
